import { Bell, Check, Mic, Volume2 } from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';
import { Dialog, Pill } from '../components/ui';
import { t } from '../i18n';
import { type PermissionState, requestMicrophone, requestNotifications, usePermissions } from '../lib/permissions';
import { ensurePushSubscription } from '../lib/push';
import { ringer } from '../lib/ringer';

const DISMISSED_KEY = 'phone-setup-dismissed';

function readDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISSED_KEY) === '1';
  } catch {
    return false;
  }
}

function rememberDismissed(): void {
  try {
    localStorage.setItem(DISMISSED_KEY, '1');
  } catch {
    // Private windows may refuse storage; then we ask again next time.
  }
}

function StatePill({ state }: { state: PermissionState }) {
  if (state === 'granted') return <Pill kind="success" dot>{t('Allowed')}</Pill>;
  if (state === 'denied') return <Pill kind="danger" dot>{t('Blocked')}</Pill>;
  if (state === 'unsupported') return <Pill dot>{t('Not available')}</Pill>;
  return <Pill kind="warning" dot>{t('Not set')}</Pill>;
}

const blockedHelp = t(
  'Chrome blocked this. Click the icon at the left of the address bar, choose Site settings, set it to Allow, then reload the page.',
);

function Row({
  icon,
  title,
  text,
  state,
  action,
  testId,
}: {
  icon: ReactNode;
  title: string;
  text: string;
  state?: PermissionState;
  action: ReactNode;
  testId: string;
}) {
  return (
    <div className="permission-row" data-testid={testId} data-state={state}>
      <div className="permission-icon">{icon}</div>
      <div className="grow">
        <div className="row" style={{ gap: '0.5rem' }}>
          <strong>{title}</strong>
          {state && <StatePill state={state} />}
        </div>
        <p className="muted">{state === 'denied' ? blockedHelp : text}</p>
      </div>
      <div>{action}</div>
    </div>
  );
}

/** Microphone, notifications and a sound check, each with its own button. */
export function PermissionRows() {
  const permissions = usePermissions();
  const [tested, setTested] = useState(false);

  async function allowMicrophone() {
    permissions.setMicrophone(await requestMicrophone());
  }

  async function allowNotifications() {
    const state = await requestNotifications();
    permissions.setNotifications(state);
    // Now notifications can also reach this browser while the app is closed.
    if (state === 'granted') void ensurePushSubscription();
  }

  const allowButton = (state: PermissionState, onClick: () => void, testId: string) =>
    state === 'granted' ? (
      <Check size={20} style={{ color: 'var(--success)' }} aria-label={t('Done')} />
    ) : state === 'prompt' ? (
      <button type="button" onClick={onClick} data-testid={testId}>
        {t('Allow')}
      </button>
    ) : null;

  return (
    <div className="permission-list">
      <Row
        icon={<Mic size={20} aria-hidden />}
        title={t('Microphone')}
        text={t('Needed to talk on calls. Use a headset for the best sound.')}
        state={permissions.microphone}
        action={allowButton(permissions.microphone, () => void allowMicrophone(), 'allow-microphone')}
        testId="permission-microphone"
      />
      <Row
        icon={<Bell size={20} aria-hidden />}
        title={t('Notifications')}
        text={t('So you see incoming calls when this tab is in the background.')}
        state={permissions.notifications}
        action={allowButton(permissions.notifications, () => void allowNotifications(), 'allow-notifications')}
        testId="permission-notifications"
      />
      <Row
        icon={<Volume2 size={20} aria-hidden />}
        title={t('Sound')}
        text={tested ? t('Did you hear a ring? If not, check your speakers or headset.') : t('Play the ring you’ll hear when someone calls.')}
        action={
          <button
            type="button"
            className="secondary"
            onClick={() => {
              ringer.test();
              setTested(true);
            }}
          >
            {t('Test ring')}
          </button>
        }
        testId="permission-sound"
      />
    </div>
  );
}

/**
 * After the first sign-in in a browser, asks for what the phone needs. Each
 * permission is requested by its own click, because Chrome may silently
 * block prompts that don't come from one.
 */
export function PhoneSetupDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <Dialog open={open} onClose={onClose} title={t('Set up your phone')} description={t('Allow these once, so calls work in this browser.')}>
      <div className="dialog-body">
        <PermissionRows />
      </div>
      <div className="dialog-footer">
        <button type="button" onClick={onClose} data-testid="finish-setup">
          {t('Done')}
        </button>
      </div>
    </Dialog>
  );
}

/** Opens the set-up dialog by itself the first time, while something is still missing. */
export function usePhoneSetup() {
  const permissions = usePermissions();
  const [open, setOpen] = useState(false);
  const needsSetup = permissions.microphone === 'prompt' || permissions.notifications === 'prompt';
  const blocked = permissions.microphone === 'denied';

  useEffect(() => {
    if (permissions.loaded && needsSetup && !readDismissed()) setOpen(true);
  }, [permissions.loaded, needsSetup]);

  return {
    open,
    show: () => setOpen(true),
    close: () => {
      rememberDismissed();
      setOpen(false);
      void permissions.refresh();
    },
    needsSetup,
    blocked,
  };
}
